import { computed } from 'vue'
import type { ComputedRef } from 'vue'
import { useRoute } from 'vue-router'
import type { MembershipSummary } from '@/api/session/access'
import { useSessionStore } from '@/apps/session/stores/session'

export interface UseOrganizationReturn {
  /** The caller's membership of the organization the URL names; `undefined` outside one. */
  organization: ComputedRef<MembershipSummary | undefined>
  /** Whether `GET /api/app/access` has not answered yet. */
  isLoading: ComputedRef<boolean>
  error: ComputedRef<Error | null>
  hasOrganization: ComputedRef<boolean>
  organizationId: ComputedRef<string | undefined>
  organizationName: ComputedRef<string>
  organizationSlug: ComputedRef<string>
  /** Always `null`: no billing customer is served. */
  billingCustomerId: ComputedRef<string | null>
  refetch: () => Promise<void>
}

/**
 * The organization the URL names, as the caller's membership lists it.
 *
 * **There is no organization resource to read.** What the session holds about one is its
 * membership — id, name, slug, and the role for display — out of `GET /api/app/access`, which
 * the router asked before any view of the organization mounted. Nothing else an organization
 * used to carry here is served: no branding, no per-organization feature, no billing customer,
 * no home-page switch.
 */
export function useOrganization(): UseOrganizationReturn {
  const route = useRoute()
  const session = useSessionStore()

  const organizationId = computed(() => {
    const id = route.params.organizationId
    return typeof id === 'string' && id !== '' ? id : undefined
  })
  const organization = computed(() =>
    session.access?.memberships.find((membership) => membership.org_id === organizationId.value),
  )

  return {
    organization,
    isLoading: computed(() => session.access === null),
    error: computed<Error | null>(() => null),
    hasOrganization: computed(() => organization.value !== undefined),
    organizationId,
    organizationName: computed(() => organization.value?.org_name ?? ''),
    organizationSlug: computed(() => organization.value?.org_slug ?? ''),
    billingCustomerId: computed<string | null>(() => null),
    refetch: async () => {
      await session.refreshAccess()
    },
  }
}
